import type { ChangeEvent, KeyboardEvent } from 'react'
import { forwardRef, useCallback, useState } from 'react'

import clsx from 'clsx'
import type { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { z } from 'zod'

import DeleteIcon from '@/assets/icons/x.svg'
import { KeyCodes } from '@/consts/keys.ts'

interface ChipInputProps extends ControllerRenderProps<FieldValues> {
  onChange: (chips: Array<string>) => void
  placeholder?: string
  inputClassName?: string
}

interface ChipProps {
  value: string
  onDelete: () => void
}

const chipListSchema = z.array(z.string())

export const ChipInput = forwardRef<HTMLInputElement, ChipInputProps>(
  ({ onChange, placeholder, inputClassName, ...props }, ref) => {
    const [chips, setChips] = useState<string[]>([])

    const [inputValue, setInputValue] = useState('')
    const [focusedChip, setFocusedChip] = useState<number | null>(null)

    const handleUpdateInput = useCallback(
      ({ target }: ChangeEvent<HTMLInputElement>) => {
        setInputValue(target.value)
      },
      [],
    )

    const addChip = useCallback(
      (value: string) => {
        const values = value
          .split(/[\s,]+/)
          .map((v) => v.trim())
          .filter(Boolean)

        const newChips = [...chips, ...values]

        setChips(newChips)
        onChange(newChips)
        setInputValue('')
      },
      [chips, onChange],
    )

    const deleteChip = useCallback(
      (index: number) => {
        const newChips = chips.filter((_chip, chipIndex) => chipIndex !== index)

        setChips(newChips)
        onChange(newChips)
      },
      [chips, onChange],
    )

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (
          (event.key === KeyCodes.SPACE || event.key === KeyCodes.COMMA) &&
          inputValue.trim() !== ''
        ) {
          event.preventDefault()

          // Ensure value is an array of strings
          const value = inputValue.trim()

          chipListSchema.parse(chips.concat(value))

          addChip(inputValue)
        }

        if (event.key === KeyCodes.BACKSPACE) {
          if (focusedChip !== null) {
            deleteChip(focusedChip)
            setFocusedChip(focusedChip - 1)
          } else if (inputValue === '' && chips.length > 0) {
            setFocusedChip(chips.length - 1)
          }
        }

        if (event.key === KeyCodes.ENTER) {
          event.preventDefault()
          if (inputValue.trim() !== '') {
            addChip(inputValue)
          }
        }
      },
      [inputValue, chips, addChip, focusedChip, deleteChip],
    )

    const handleFocusOut = useCallback(() => {
      if (inputValue.trim() !== '') {
        addChip(inputValue)
      }
    }, [inputValue, addChip])

    const handlePaste = useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = event.clipboardData.getData('text')

        if (pastedData) {
          event.preventDefault()
          addChip(pastedData)
        }
      },
      [addChip],
    )

    return (
      <div className="relative flex flex-wrap rounded-md border p-2">
        {chips.map((chip, index) => (
          <Chip
            key={index}
            value={chip}
            onDelete={() => deleteChip(index)}
          />
        ))}

        <input
          {...props}
          ref={ref}
          type="text"
          value={inputValue}
          onPaste={handlePaste}
          onChange={handleUpdateInput}
          onKeyDown={handleKeyDown}
          className={clsx(
            'h-[34px] flex-1 border-none bg-card text-title outline-none mx-1',
            inputClassName,
          )}
          placeholder={placeholder}
          onBlur={handleFocusOut}
          onFocus={() => setFocusedChip(null)}
        />
      </div>
    )
  },
)

const Chip = ({ value, onDelete }: ChipProps) => {
  return (
    <div className="max-w-[500px] m-1 flex items-center gap-1 rounded-md border bg-secondary-50 px-2 py-0.5 text-title dark:bg-secondary-600">
      <span className="text-ellipsis overflow-hidden whitespace-nowrap">
        {value}
      </span>
      <DeleteIcon
        onClick={onDelete}
        width={15}
        height={15}
        className="hover:cursor-pointer min-w-[15px]"
      />
    </div>
  )
}

ChipInput.displayName = 'ChipInput'
